import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Search, Filter, Star, Calendar, MapPin, Building, User, Edit, Settings, Briefcase, Globe, Clock, DollarSign } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import RecruitingProjectForm from '@/components/dashboard/recruiting-project-form';
import InterviewForm from '@/components/interviews/interview-form';
import { Separator } from '@/components/ui/separator';
import { ApplicationContext } from '@/components/profile/application-context';

export default function ApplicationsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentMode, hasLicense } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<any>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [statusMapping, setStatusMapping] = useState<any>({});
  const [currentApplicationId, setCurrentApplicationId] = useState<string | null>(null);
  const [interviewFormOpen, setInterviewFormOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (!user || !id) return;

    const checkAccess = async () => {
      // Check if user has hire license
      if (currentMode === 'hire' && hasLicense('hire')) {
        setHasAccess(true);
        fetchData();
        return;
      }

      // Check if user is a company admin or member with recruiter role
      try {
        const { data, error } = await supabase
          .from('company_users')
          .select('role, is_active')
          .eq('user_id', user.id)
          .eq('is_active', true);
          
        if (!error && data && data.length > 0) {
          // Check if user has recruiter or admin role
          const hasRecruiterRole = data.some(
            record => record.role === 'recruiter' || record.role === 'admin' || record.role === 'hiring-manager'
          );
          
          if (hasRecruiterRole) {
            setHasAccess(true);
            fetchData();
            return;
          }
        }
      } catch (error) {
        console.error('Error checking company role:', error);
      }
      
      // If we get here, user doesn't have access
      toast({
        variant: 'destructive',
        title: 'Access Denied',
        description: 'You must be a recruiter to access this page.',
      });
      navigate('/dashboard');
    };

    checkAccess();
  }, [user, id, navigate, toast, currentMode, hasLicense]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // First fetch the job details with interview process and status mapping
      const { data: jobData, error: jobError } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          status_mapping
        `)
        .eq('id', id)
        .single();

      if (jobError) throw jobError;
      if (!jobData) throw new Error('Job not found');

      setJob(jobData);
      setStatusMapping(jobData.status_mapping || {});

      // Then fetch applications with related data
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('applications')
        .select(`
          *,
          profiles (
            id,
            full_name,
            title,
            location,
            email
          )
        `)
        .eq('job_id', id)
        .order('application_date', { ascending: false });

      if (applicationsError) throw applicationsError;
      
      // Set applications and find the first active application
      const apps = applicationsData || [];
      setApplications(apps);
      
      // Find the first application that's in progress
      const activeApp = apps.find(app => 
        app.status === 'interviewed' || 
        app.status === 'reviewing'
      );
      
      // If no active application, use the most recent one
      setCurrentApplicationId(activeApp?.id || (apps.length > 0 ? apps[0].id : null));

    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to load data',
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      // Start a transaction
      const { error: updateError } = await supabase
        .from('applications')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);
        
      if (updateError) throw updateError;
      
      // Update local state
      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
      
      // Set this as the current application being viewed
      setCurrentApplicationId(applicationId);

      toast({
        title: "Status Updated",
        description: `Application status changed to ${statusMapping[newStatus]?.label || newStatus}`,
      });
      
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update application status.",
        variant: "destructive"
      });
    }
  };

  const handleViewProfile = (applicant: any) => {
    navigate(`/evaluate/${applicant.profiles.id}`, { 
      state: { 
        fromApplicants: true,
        jobId: id,
        jobTitle: job?.title,
        applicationId: applicant.id
      }
    });
  };

  const handleScheduleInterview = (application: any) => {
    setSelectedApplication(application);
    setInterviewFormOpen(true);
  };

  // Filter and sort applications
  const filteredApplications = applications.filter(application => {
    if (statusFilter !== 'all' && application.status !== statusFilter) {
      return false;
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = application.profiles?.full_name?.toLowerCase().includes(query);
      const titleMatch = application.profiles?.title?.toLowerCase().includes(query);
      return nameMatch || titleMatch;
    }
    
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.application_date).getTime() - new Date(a.application_date).getTime();
      case 'name':
        return (a.profiles?.full_name || '').localeCompare(b.profiles?.full_name || '');
      case 'status':
        return (a.status || '').localeCompare(b.status || '');
      default:
        return 0;
    }
  });

  if (!hasAccess) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You need a recruiter license or appropriate company role to view applications.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{job?.title}</h1>
            <p className="text-muted-foreground">
              View and manage applications
            </p>
          </div>
        </div>

        {/* Job Details Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between gap-6">
              <div className="space-y-4 flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="font-medium text-lg">{job?.title}</h2>
                  <Button variant="outline" onClick={() => setEditFormOpen(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Project
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Department:</span>
                      <span>{job?.department}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Location:</span>
                      <span>{job?.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Type:</span>
                      <span>{job?.employment_type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Remote Option:</span>
                      <span>{job?.remote_option}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Experience Level:</span>
                      <span>{job?.experience_level}</span>
                    </div>
                    {job?.salary_range && (
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Salary Range:</span>
                        <span>{job?.salary_range}</span>
                      </div>
                    )}
                    {job?.application_deadline && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Deadline:</span>
                        <span>{format(new Date(job.application_deadline), 'MMM d, yyyy')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4" />

                {job?.skills?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Required Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill: string, i: number) => (
                        <Badge key={i} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 items-end">
                <Badge variant="secondary" className="gap-1">
                  <User className="h-3.5 w-3.5" />
                  {applications.length} {applications.length === 1 ? 'applicant' : 'applicants'}
                </Badge>
                {job?.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Application Context */}
        {currentApplicationId && (
          <ApplicationContext 
            profileId={applications.find(app => app.id === currentApplicationId)?.profiles?.id}
          />
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name or title..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(statusMapping).map(([status, data]: [string, any]) => (
                  <SelectItem key={status} value={status}>
                    {data.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date Applied</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setSortBy('date');
              }}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-medium">No Applications Found</h3>
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== 'all'
                  ? "No applications match your search or filter criteria."
                  : "No applications have been submitted yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredApplications.map((application) => (
              <Card key={application.id} className="hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {application.profiles?.full_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {application.profiles?.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Applied {format(new Date(application.application_date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={application.status}
                        onValueChange={(value) => updateApplicationStatus(application.id, value)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusMapping).map(([status, data]: [string, any]) => (
                            <SelectItem key={status} value={status}>
                              {data.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline"
                        onClick={() => handleScheduleInterview(application)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule
                      </Button>
                      <Button onClick={() => handleViewProfile(application)}>
                        View Profile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Project Form */}
      <RecruitingProjectForm
        open={editFormOpen}
        onOpenChange={setEditFormOpen}
        project={job}
        onSuccess={() => {
          // Refresh job data after edit
          if (id) {
            const fetchJob = async () => {
              const { data, error } = await supabase
                .from('recruiting_projects')
                .select(`
                  *,
                  status_mapping
                `)
                .eq('id', id)
                .single();
              
              if (!error && data) {
                setJob(data);
                setStatusMapping(data.status_mapping || {});
              }
            };
            fetchJob();
          }
        }}
      />

      {/* Interview Form */}
      <InterviewForm
        open={interviewFormOpen}
        onOpenChange={setInterviewFormOpen}
        job={job}
        application={selectedApplication}
        onSuccess={() => {
          setInterviewFormOpen(false);
          toast({
            title: 'Success',
            description: 'Interview scheduled successfully',
          });
        }}
      />
    </div>
  );
}